import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCallHistory } from '@/features/calls/hooks/useCallHistory';
import { callStatusInfo } from '@/features/calls/lib/callStatusInfo';
import { useMyDashboardStats } from '../hooks/useProfessionalProfile';

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatRecentDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ConsultantDashboardHome() {
  const { user } = useAuth();
  const { data: stats, isLoading } = useMyDashboardStats();
  const { data: recent, isLoading: recentLoading } = useCallHistory({ limit: 5 });
  const recentSessions = recent?.data ?? [];

  const firstName = user?.profile?.firstName || 'Professionista';

  const todayFormatted = new Date().toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white">Benvenuto, {firstName}</h1>
          <p className="mt-1 text-brand-cyan">{todayFormatted}</p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
            <circle cx="160" cy="40" r="60" fill="white" />
            <circle cx="100" cy="140" r="80" fill="white" />
            <circle cx="180" cy="160" r="40" fill="white" />
          </svg>
        </div>
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
            }
            label="Consulenze Totali"
            value={stats?.totalConsultations ?? 0}
            color="primary"
          />
          <StatsCard
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
            }
            label="Valutazione Media"
            value={stats?.rating != null ? stats.rating.toFixed(1) : '--'}
            color="secondary"
          />
          <StatsCard
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
              </svg>
            }
            label="Guadagni Totali"
            value={formatEur(stats?.totalEarnings ?? 0)}
            color="warning"
          />
          <StatsCard
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            }
            label="Recensioni"
            value={stats?.reviewCount ?? 0}
            color="info"
          />
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-neutral-900">Azioni Rapide</h2>
        <div className="grid grid-cols-1 gap-4">
          <QuickActionCard
            to="/dashboard/profile"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            title="Modifica Profilo"
            description="Aggiorna le tue informazioni e la tua presentazione"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Attivita Recenti</CardTitle>
          <CardDescription>Le tue ultime consulenze</CardDescription>
        </CardHeader>
        <CardContent>
          {recentLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : recentSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 rounded-full bg-neutral-100 p-4">
                <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-medium text-neutral-700">Nessuna attivita recente</p>
              <p className="mt-1 text-sm text-neutral-600">Le tue consulenze appariranno qui</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSessions.map((session) => {
                const statusInfo = callStatusInfo(session);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const caller = session.callerId as any;
                const callerName = caller?.profile
                  ? `${caller.profile.firstName ?? ''} ${caller.profile.lastName ?? ''}`.trim()
                  : caller?.email ?? 'Utente';
                return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">{callerName}</p>
                      <p className="text-xs text-neutral-500">
                        {session.callType === 'video' ? 'Videochiamata' : 'Chiamata'} · {formatRecentDate(session.initiatedAt)}
                      </p>
                    </div>
                    {session.proEarning != null && session.proEarning > 0 && (
                      <span className="shrink-0 text-sm font-semibold text-green-600">
                        +€{session.proEarning.toFixed(2)}
                      </span>
                    )}
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                );
              })}
              <div className="pt-2 text-center">
                <Link to="/dashboard/consultations" className="text-sm font-medium text-brand-teal hover:underline">
                  Vedi tutte le consulenze
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const colorMap = {
  primary: {
    border: 'border-l-brand-teal',
    bg: 'bg-brand-teal-50',
    text: 'text-brand-teal-600',
  },
  secondary: {
    border: 'border-l-brand-cyan',
    bg: 'bg-brand-cyan-light',
    text: 'text-brand-cyan-dark',
  },
  warning: {
    border: 'border-l-brand-orange',
    bg: 'bg-brand-orange-50',
    text: 'text-brand-orange-600',
  },
  info: {
    border: 'border-l-brand-navy',
    bg: 'bg-brand-navy-50',
    text: 'text-brand-navy',
  },
} as const;

type CardColor = keyof typeof colorMap;

function StatsCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: CardColor;
}) {
  const colors = colorMap[color];

  return (
    <div className={`rounded-xl border border-neutral-200 border-l-4 ${colors.border} bg-white p-5`}>
      <div className="flex items-center gap-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors.bg} ${colors.text}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-neutral-900">{value}</p>
          <p className="text-sm text-neutral-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

function QuickActionCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-brand-teal/30"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-teal-50 text-brand-teal-600">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-neutral-900">{title}</h3>
        <p className="text-sm text-neutral-600">{description}</p>
      </div>
      <svg className="h-5 w-5 shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
